import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  MapPin,
  ShoppingCart,
  Check,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth, API } from "../App";

const UpgradePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, loading: authLoading } = useAuth();
  const upgradeToken = searchParams.get("t");

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!upgradeToken) {
      setError("Missing upgrade token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/upgrades/${upgradeToken}`)
      .then((res) => setOffer(res.data))
      .catch((e) => {
        setError(
          e.response?.status === 404
            ? "This upgrade link is invalid or has expired."
            : e.response?.data?.detail || "Failed to load upgrade offer."
        );
      })
      .finally(() => setLoading(false));
  }, [upgradeToken]);

  // Redirect to login if unauthenticated (preserve t= param)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth?redirect=${encodeURIComponent(`/upgrade?t=${upgradeToken}`)}`);
    }
  }, [authLoading, user, navigate, upgradeToken]);

  const toggleCountry = (name) => {
    setSelectedCountries((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  const priceOption = offer?.options?.find((o) => o.add_count === selectedCountries.length);
  const price = priceOption?.price_dollars ?? 0;
  const tooMany = offer && selectedCountries.length > offer.options.length;

  const handleCheckout = async () => {
    if (selectedCountries.length === 0) {
      toast.error("Please pick at least one country to add");
      return;
    }
    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/upgrades/checkout`,
        {
          upgrade_token: upgradeToken,
          additional_country_names: selectedCountries,
          origin_url: window.location.origin,
          payment_method: paymentMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">Upgrade unavailable</h2>
          <p className="text-muted-foreground mb-6">
            {error || "No upgrade offer found."}
          </p>
          <Button onClick={() => navigate("/dashboard")} data-testid="upgrade-back-dashboard-btn">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="upgrade-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
          data-testid="upgrade-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 rounded-2xl border border-primary/20 p-6 md:p-8 mb-8">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="h-6 w-6 text-secondary shrink-0 mt-1" />
            <div>
              <h1
                className="text-2xl md:text-3xl font-bold text-primary mb-2"
                data-testid="upgrade-title"
              >
                Still weighing your options?
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                You already own the{" "}
                <span className="font-semibold text-primary">
                  {offer.original_country_names.join(", ")}
                </span>{" "}
                guide. Add 1 or 2 more countries at the bundle price — not full price.
              </p>
            </div>
          </div>

          {/* Pricing options */}
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {offer.options.map((opt) => (
              <div
                key={opt.add_count}
                className={`rounded-xl p-4 border-2 transition-colors ${
                  selectedCountries.length === opt.add_count
                    ? "border-primary bg-primary/5"
                    : "border-primary/10 bg-card"
                }`}
                data-testid={`upgrade-option-${opt.add_count}`}
              >
                <p className="text-sm text-muted-foreground">
                  Add {opt.add_count} {opt.add_count === 1 ? "country" : "countries"}
                </p>
                <p className="text-2xl font-bold text-primary">+${opt.price_dollars}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Total bundle: {opt.new_total}-country
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Country picker */}
        <div className="bg-card rounded-2xl border border-primary/10 p-6 md:p-8 mb-6">
          <h2 className="text-xl font-bold text-primary mb-2">
            Pick from your top matches
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            These are the countries from your most recent recommendations. Select 1
            or 2 to add.
          </p>

          {offer.recommendations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No other matches available. Please retake the questionnaire for fresh
              picks.
            </div>
          ) : (
            <div className="space-y-3">
              {offer.recommendations.map((rec) => {
                const isSelected = selectedCountries.includes(rec.country_name);
                const isDisabled =
                  !isSelected &&
                  selectedCountries.length >=
                    Math.max(...offer.options.map((o) => o.add_count));
                return (
                  <button
                    type="button"
                    key={rec.country_name}
                    onClick={() => !isDisabled && toggleCountry(rec.country_name)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-colors text-left ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : isDisabled
                        ? "border-primary/10 bg-muted/30 opacity-50 cursor-not-allowed"
                        : "border-primary/10 bg-card hover:border-primary/30"
                    }`}
                    data-testid={`upgrade-country-${rec.country_name
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => !isDisabled && toggleCountry(rec.country_name)}
                    />
                    <MapPin className="h-5 w-5 text-secondary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-primary">{rec.country_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {rec.summary}
                      </p>
                    </div>
                    {rec.match_score && (
                      <span className="font-mono text-sm font-bold text-primary shrink-0">
                        {rec.match_score}%
                      </span>
                    )}
                    {isSelected && <Check className="h-5 w-5 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Checkout bar */}
        {selectedCountries.length > 0 && (
          <div className="bg-card rounded-2xl border-2 border-accent p-6 flex flex-col md:flex-row items-center justify-between gap-4 sticky bottom-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Adding {selectedCountries.length}{" "}
                {selectedCountries.length === 1 ? "country" : "countries"}
              </p>
              <p className="text-3xl font-bold text-accent" data-testid="upgrade-total-price">
                ${price}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="px-3 py-2 rounded-lg border border-primary/20 bg-white text-sm"
                data-testid="upgrade-payment-method-select"
              >
                <option value="stripe">Credit Card</option>
                <option value="paypal">PayPal</option>
              </select>
              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading || tooMany}
                className="btn-animated bg-accent hover:bg-accent/90 text-white gap-2 px-6"
                data-testid="upgrade-checkout-btn"
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShoppingCart className="h-4 w-4" />
                )}
                Complete upgrade
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradePage;
